import { FormErrors } from "./form-errors";
import { PasswordInput } from "./password-input";
import { GalleryVerticalEnd } from "lucide-react";
import { Form, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { newPasswordPath, oauthGooglePath, loginPath } from "@/routes";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { t } = useTranslation();
  const { app_name: appName, errors } = usePage().props;
  const handleGoogleLogin = () => {
    window.location.href = oauthGooglePath();
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Form action={loginPath()} method="post" disableWhileProcessing>
        {({ processing }) => (
          <FieldGroup>
            <FormErrors
              errors={errors}
              targets={{
                email_address: "email_address",
                password: "password",
                password_confirmation: "password_confirmation",
              }}
            />
            <div className="flex flex-col items-center gap-2 text-center">
              <Link
                href={loginPath()}
                className="flex flex-col items-center gap-2 font-medium"
              >
                <div className="flex size-8 items-center justify-center rounded-md">
                  <GalleryVerticalEnd className="size-6" />
                </div>
                <span className="sr-only">{appName}</span>
              </Link>
              <h1 className="text-page font-display">
                {t("auth.login_title")} · {appName}
              </h1>
              <FieldDescription>
                {t("auth.no_account")}{" "}
                <Link href="/sign_up">{t("auth.signup")}</Link>
              </FieldDescription>
            </div>
            <Field>
              <FieldLabel htmlFor="email_address">{t("auth.email")}</FieldLabel>
              <Input
                id="email_address"
                name="email_address"
                type="email"
                autoComplete="email"
                placeholder="m@example.com"
                required
              />
            </Field>
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel htmlFor="password">{t("auth.password")}</FieldLabel>
                <Link href={newPasswordPath()} className="text-sm font-medium">
                  {t("auth.forgot_password")}
                </Link>
              </div>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="current-password"

                placeholder="********"
                required
              />
            </Field>
            <Field>
              <Button
                type="submit"
                disabled={processing}
                aria-busy={processing}
              >
                {processing && <Spinner />}
                {t("auth.login")}
              </Button>
            </Field>
            <FieldSeparator>{t("auth.or")}</FieldSeparator>
            <Field className="grid gap-4 sm:grid-cols-1">
              <Button
                variant="outline"
                type="button"
                onClick={handleGoogleLogin}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                    fill="currentColor"
                  />
                </svg>
                {t("auth.continue_google")}
              </Button>
            </Field>
          </FieldGroup>
        )}
      </Form>
    </div>
  );
}
